// Builds the "Privacy file" the user downloads after creating a survey.
//
// Format: a self-contained HTML file. Doppelklick öffnet im Browser, die
// Links sind klickbar. Eingebetteter <script type="application/json"> macht
// die Datei zusätzlich maschinenlesbar — falls wir später einen Re-Import
// bauen ("Datei reinziehen → Admin-Bereich öffnen").
//
// No external resources, no remote calls — all info lives in the file.

import type { Appointment, FinderConfig } from './types';

export type PrivacyFileInput = {
  title: string | null;
  origin: string; // z. B. "https://slot.gotyou.at"
  surveyId: string;
  adminToken: string;
  voterToken: string;
  expiresAt: number | null; // unix seconds
  createdAt: number; // unix seconds
  config: FinderConfig;
  slots: Appointment[];
};

export function buildPrivacyFile(input: PrivacyFileInput): string {
  const adminUrl = `${input.origin}/admin/${input.adminToken}`;
  const voterUrl = `${input.origin}/vote/${input.voterToken}`;
  const titleText = input.title?.trim() || 'Slot Umfrage';
  const created = formatDateTime(input.createdAt);
  const expires = input.expiresAt === null
    ? 'nie (manuelle Löschung)'
    : formatDateTime(input.expiresAt);

  const machineData = {
    version: 1,
    kind: 'slot-privacy-file',
    surveyId: input.surveyId,
    adminToken: input.adminToken,
    voterToken: input.voterToken,
    adminUrl,
    voterUrl,
    expiresAt: input.expiresAt,
    createdAt: input.createdAt,
    title: input.title,
    config: input.config,
    slots: input.slots,
  };
  const machineDataJson = JSON.stringify(machineData, null, 2);

  return `<!doctype html>
<html lang="de">
<head>
  <meta charset="utf-8">
  <title>${escapeHtml(titleText)} – Slot · gotyou.at</title>
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      max-width: 38rem;
      margin: 2rem auto;
      padding: 0 1rem 4rem;
      color: #1f2430;
      line-height: 1.5;
    }
    h1 { font-size: 1.4rem; margin-bottom: 0.25rem; }
    .meta { color: #6b7180; font-size: 0.85rem; margin-bottom: 2rem; }
    section {
      border: 1px solid #dfe2e8;
      border-radius: 8px;
      padding: 1rem 1.25rem;
      margin-bottom: 1rem;
      background: #fff;
    }
    section h2 {
      font-size: 0.78rem;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: #6b7180;
      margin: 0 0 0.5rem;
    }
    .url {
      display: block;
      padding: 0.65rem 0.8rem;
      background: #f5f6f8;
      border-radius: 6px;
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      font-size: 0.85rem;
      word-break: break-all;
      text-decoration: none;
      color: #1f2430;
      margin-bottom: 0.5rem;
    }
    .url:hover { background: #eaf2ff; color: #2563eb; }
    .hint { font-size: 0.85rem; color: #6b7180; margin: 0; }
    .warning {
      background: #fff8e1;
      border-color: #f4cf6b;
      color: #6b4f00;
    }
    .warning h2 { color: #6b4f00; }
    .badge-admin { background: #fde4e4; color: #8a1313; }
    .badge-voter { background: #d6f0df; color: #1f7a3d; }
    .badge {
      display: inline-block;
      padding: 0.1rem 0.5rem;
      border-radius: 999px;
      font-size: 0.7rem;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      margin-left: 0.5rem;
      vertical-align: middle;
    }
    footer {
      margin-top: 2rem;
      font-size: 0.75rem;
      color: #6b7180;
      text-align: center;
    }
  </style>
</head>
<body>
  <h1>${escapeHtml(titleText)}</h1>
  <p class="meta">Erstellt am ${escapeHtml(created)} · Slot · gotyou.at</p>

  <section class="warning">
    <h2>⚠ Wichtig</h2>
    <p class="hint" style="color:inherit">
      Diese Datei ist dein einziger Zugang zur Verwaltung dieser Umfrage.
      Wenn du sie verlierst, ist der Admin-Bereich nicht mehr erreichbar
      (die Umfrage selbst läuft weiter). Speichere die Datei sicher und teile
      sie mit niemandem.
    </p>
    <p class="hint" style="color:inherit; margin-top:0.5rem">
      Automatische Löschung: <strong>${escapeHtml(expires)}</strong>
    </p>
  </section>

  <section>
    <h2>Admin-Zugang <span class="badge badge-admin">Nicht teilen</span></h2>
    <a class="url" href="${escapeAttr(adminUrl)}">${escapeHtml(adminUrl)}</a>
    <p class="hint">Über diesen Link siehst du alle Antworten und kannst die
      Umfrage bearbeiten oder löschen.</p>
  </section>

  <section>
    <h2>Teilnehmer-Link <span class="badge badge-voter">Teilen</span></h2>
    <a class="url" href="${escapeAttr(voterUrl)}">${escapeHtml(voterUrl)}</a>
    <p class="hint">Schicke diesen Link an alle, die abstimmen sollen.
      Es ist kein Account nötig.</p>
  </section>

  <footer>Slot · gotyou.at · Privacy-First Terminfindung</footer>

  <script type="application/json" id="slot-data">
${escapeForJsonScript(machineDataJson)}
  </script>
</body>
</html>
`;
}

function formatDateTime(unixSeconds: number): string {
  const d = new Date(unixSeconds * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}, ${pad(d.getHours())}:${pad(d.getMinutes())} Uhr`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeAttr(s: string): string {
  return escapeHtml(s);
}

/** Inside a <script type="application/json"> block, only `</` is dangerous. */
function escapeForJsonScript(s: string): string {
  return s.replace(/<\//g, '<\\/');
}

export function downloadPrivacyFile(input: PrivacyFileInput): void {
  const html = buildPrivacyFile(input);
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filenameFor(input);
  a.rel = 'noopener';
  document.body.append(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function filenameFor(input: PrivacyFileInput): string {
  const slug = (input.title ?? 'umfrage')
    .toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40) || 'umfrage';
  return `slot-${slug}-${input.adminToken.slice(0, 8)}.html`;
}
